/**
 * The canvas destinations and the URL each one lives at.
 *
 * Its own module rather than a corner of `SidebarNavList`, because the app root
 * mounts these paths and the sidebar only draws rows for them. A route table
 * pulled in through a React component would make every consumer of the paths
 * load the sidebar too.
 */

import { RESERVED_SKILL_NAMES } from "@/types/skills.ts";

/** The canvas destinations the sidebar can pivot to. */
export type Pivot =
  | "newchat"
  | "calendar"
  | "tasks"
  | "routines"
  | "skills"
  | "contacts"
  | "connectors"
  | "library";

/**
 * Each destination's name. Exported because a breadcrumb naming where a thread
 * was drilled into from has to read exactly like the row that was clicked, and
 * two tables would drift.
 */
export const PIVOT_LABELS: Record<Pivot, string> = {
  newchat: "New chat",
  calendar: "Calendar",
  tasks: "Tasks",
  routines: "Routines",
  skills: "Skills",
  contacts: "People",
  connectors: "Connectors",
  library: "Search",
};

/**
 * Where each destination lives. Clicking a row navigates here, and the path is
 * what tells the page which destination to show — the canvas is somewhere you
 * go, not a mode a query param puts the home screen into.
 *
 * New chat is the app's home, so its path is `/` rather than a name of its own.
 * That keeps one URL per destination: a bare `/` and a "New chat" click land on
 * the same address instead of two that render the same screen.
 *
 * A `Record<Pivot, …>`, so a destination cannot join the union without an
 * address, which is the same guarantee `PIVOT_LABELS` gives for its name.
 *
 * Every path here answers for every account, including the two whose rows are
 * behind Statsig gates. That is deliberate, and it is the settings screen's own
 * arrangement: `/settings/skills` still resolves for a saved link after the gear
 * stopped listing it. A gate decides what the app offers, not what it permits —
 * the client cannot enforce the difference anyway, since anyone can flip a gate
 * from the console. Where a screen must be denied rather than unlisted, the
 * denial belongs in the handler behind it; see the note on the routine state
 * endpoint in `api/src/handlers/routines.ts`.
 */
export const PIVOT_PATHS: Record<Pivot, string> = {
  newchat: "/",
  calendar: "/calendar",
  tasks: "/tasks",
  routines: "/routines",
  skills: "/skills",
  contacts: "/contacts",
  connectors: "/connectors",
  library: "/library",
};

/**
 * Where each destination that can single out one of its own things spells that
 * thing's URL: `/contacts/42`, `/routines/3f1c`, `/skills/inbox-triage`,
 * `/calendar/event/abc`.
 *
 * Singling one out is a move to another address, not a mode the destination is
 * put into. Before this the open item lived in the canvas view's own `useState`
 * — a contact filled the column while the URL still read `/contacts`, an event's
 * popover stood open while it still read `/calendar`. Nothing to link to,
 * nothing a reload or the browser's Back gesture could restore (BIG-7804).
 *
 * A prefix each rather than a rule, because the calendar's is not its own path
 * plus an id: an event is `/calendar/event/:id`, which is the address it already
 * answered to before any of this. One table read in both directions, so what
 * {@link pivotItemPath} writes is what {@link pivotItemForPath} reads back and
 * the two cannot drift.
 */
const ITEM_PATH_PREFIXES = {
  contacts: PIVOT_PATHS.contacts,
  routines: PIVOT_PATHS.routines,
  skills: PIVOT_PATHS.skills,
  // `/tasks/:id`, which the brief's "On your plate" links open.
  tasks: PIVOT_PATHS.tasks,
  // Mirrors `eventDetailPath` in `calendar-url.ts`, which is what the rest of
  // the app builds an event link with. A test holds the two together.
  calendar: `${PIVOT_PATHS.calendar}/event`,
} as const satisfies Partial<Record<Pivot, string>>;

/** The destinations that can single out one of their own things. */
export const ITEM_PIVOTS = Object.keys(ITEM_PATH_PREFIXES) as readonly ItemPivot[];

/** A destination that can single out one of its own things. */
export type ItemPivot = keyof typeof ITEM_PATH_PREFIXES;

/**
 * Segments under an item destination that name something other than an item.
 *
 * `/skills/new` is the create form, which is the settings screen's — the canvas
 * has no create form, because a skill is described to the assistant rather than
 * filled into one. Without this it would read as a skill named "new" and the
 * canvas would go looking for one.
 *
 * The skills entry is the shared set the API already refuses to name a skill,
 * rather than a second list saying the same thing: that set exists precisely
 * because a name here shadows a route, so a name added to it has to stop being
 * an item id in the same breath.
 */
const RESERVED_ITEM_SEGMENTS: Partial<Record<ItemPivot, ReadonlySet<string>>> = {
  skills: RESERVED_SKILL_NAMES,
};

/** Whether a destination is one that drills into an item of its own. */
export function isItemPivot(pivot: Pivot): pivot is ItemPivot {
  return (ITEM_PIVOTS as readonly Pivot[]).includes(pivot);
}

/** Escape a literal path so it can sit inside a `RegExp` source. */
const escapeForPattern = (literal: string) => literal.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * The URL for one thing a destination has singled out.
 *
 * Every id these destinations use is URL-safe already — a contact's and a user
 * routine's are UUIDs, a system routine's is its stable slug (the `system:`
 * sentinel is on `routineEntId`, not on the `id` the list opens by), a skill's
 * name is `^[a-z0-9-]+$`, and an event's is its source ent id. The encode is so
 * that a later id which is not still round-trips through
 * {@link pivotItemForPath}, rather than silently producing a path that reads
 * back as a different item or as none.
 */
export function pivotItemPath(pivot: ItemPivot, itemId: string): string {
  return `${ITEM_PATH_PREFIXES[pivot]}/${encodeURIComponent(itemId)}`;
}

/**
 * The destination and item a path names, or null when it names no item — a bare
 * destination, a reserved segment, or anything else.
 *
 * The inverse of {@link pivotItemPath}, so what the canvas wrote is what it
 * reads back. Split at the last slash rather than the first, because a prefix is
 * not always one segment: `/calendar/event/abc` is an event, while
 * `/contacts/a/b` is nobody's.
 */
export function pivotItemForPath(path: string): { pivot: ItemPivot; itemId: string } | null {
  const normalized = path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
  const lastSlash = normalized.lastIndexOf("/");
  if (lastSlash <= 0) return null;
  const prefix = normalized.slice(0, lastSlash);
  const rawItemId = normalized.slice(lastSlash + 1);
  if (!rawItemId) return null;
  const pivot = ITEM_PIVOTS.find((candidate) => ITEM_PATH_PREFIXES[candidate] === prefix);
  if (!pivot) return null;
  let itemId: string;
  try {
    itemId = decodeURIComponent(rawItemId);
  } catch {
    // A malformed escape (`/skills/%zz`) is not an item. Caught rather than
    // left to throw: this runs while the page renders, so a hand-mangled URL
    // would take the whole app down instead of falling through to the list.
    return null;
  }
  if (RESERVED_ITEM_SEGMENTS[pivot]?.has(itemId)) return null;
  return { pivot, itemId };
}

/**
 * One pattern matching every destination's path — the single route the app root
 * mounts the canvas on.
 *
 * One route rather than one per path, and that is load-bearing. `Switch`
 * renders whichever child matches, and React reconciles that child by identity:
 * a `Route` each would hand it a different element every time you moved between
 * destinations, so it would tear the canvas down and build a new one. The
 * remount is visible — the right sidebar animates out from its unmeasured width
 * on arrival — and it also throws away the scroll position, the open card, and
 * every query the column had already loaded. Matching by pattern keeps one
 * element, so moving between destinations re-renders the canvas instead of
 * replacing it.
 *
 * Built from the table, so a destination cannot end up with a path the router
 * does not answer on. Longest first so the branch that matches is tried first,
 * and a trailing slash is optional to match what `pivotForPath` forgives.
 *
 * A thread's own page is here too. It is not a pivot — no sidebar row names it,
 * and `pivotForPath` still answers null — but it is a destination the same shell
 * shows, in the same column, with the same list beside it. Routing it anywhere
 * else would tear that shell down to render a conversation and build it again on
 * the way back.
 *
 * Anchored, so only the bare thread page joins: `/threads/:id/participants` and
 * the `/threads/__shell` placeholder keep their own routes, which sit ahead of
 * this one in the Switch.
 *
 * An item destination's own item joins for the same reason a thread does, and
 * it is the same reason one route serves every destination: drilling into a
 * contact from the contacts list must not remount the canvas around it. That is
 * also why these paths are here rather than on routes of their own — a separate
 * `Route` would tear the column down to open a contact and build it again on
 * the way back to the list.
 */
export const PIVOT_ROUTE_PATTERN = new RegExp(
  `^(?:${[
    ...Object.values(PIVOT_PATHS)
      .slice()
      .sort((a, b) => b.length - a.length)
      .map(escapeForPattern),
    // No leading zero, and short enough to be a real id, so `/threads/0` and a
    // twenty-digit URL fall through to the 404 rather than mounting the shell
    // on a thread that cannot exist. The exact ceiling is `parseThreadId`'s,
    // applied when the id is read.
    "/threads/[1-9]\\d{0,9}",
    // One segment past the prefix, so the settings copies of these screens are
    // untouched. Reserved segments are excluded here as well as in
    // `pivotItemForPath`, so `/skills/new` falls through to the route that
    // still redirects it to the create form.
    ...ITEM_PIVOTS.map((pivot) => {
      const reserved = [...(RESERVED_ITEM_SEGMENTS[pivot] ?? [])];
      const exclusion = reserved.length
        ? `(?!(?:${reserved.map(escapeForPattern).join("|")})/?$)`
        : "";
      // A segment `pivotItemForPath` can actually decode, so the two agree on
      // what an item path is. `[^/]+` accepted a malformed escape like
      // `/skills/%zz`, which the parser then refused — and because this route
      // had already claimed the URL, the canvas fell back to New chat instead
      // of letting the catch-all answer with a 404.
      return `${escapeForPattern(ITEM_PATH_PREFIXES[pivot])}/${exclusion}(?:[^/%]|%[0-9A-Fa-f]{2})+`;
    }),
  ].join("|")})/?$`,
);

const PIVOT_BY_PATH = new Map<string, Pivot>(
  (Object.entries(PIVOT_PATHS) as [Pivot, string][]).map(([pivot, path]) => [path, pivot]),
);

/**
 * The destination a path names, or null for a path that is not one — a thread's
 * own page, settings, an artifact. Callers decide what "not a destination"
 * means for them; the page falls back to home.
 *
 * A trailing slash is trimmed first so `/routines/` answers like `/routines`.
 * Wouter matches the two separately, and a link written by hand should not land
 * on a different screen than the sidebar's own row does.
 */
export function pivotForPath(path: string): Pivot | null {
  const normalized = path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
  return PIVOT_BY_PATH.get(normalized) ?? null;
}
